/**
 * lib/floorplan2dHelpers.js — V2.0.61 monolith split.
 *
 * Pure helpers extracted from FloorPlan2D.jsx. Each is dependency-free
 * and easily unit-testable. Anything that touches React state, refs, or
 * the DOM stays in the component.
 */

import { recommendInteriorOpening, recommendExteriorOpening } from "./openingRecommendations";

/**
 * Format a numeric area as "1,234 sqft", returning "" for non-finite input.
 * V1.7 — used for room area chips on the 2D plan.
 */
export function fmtSqft(n) {
  return typeof n === "number" && Number.isFinite(n) ? `${Math.round(n)} sqft` : "";
}

/**
 * Architectural feet-inches formatter for the Compare view.
 * Returns e.g. `12'-0"` for whole feet, `7'-6"` otherwise, "" for invalid.
 * V1.7.6 — mirrors the backend `fmt_ft_in` in architectural_pdf_geometry.py.
 */
export function fmtFtIn(metres) {
  if (!Number.isFinite(metres) || metres <= 0) return "";
  const totalIn = metres * 39.3701;
  let ft = Math.floor(totalIn / 12);
  let inches = Math.round(totalIn - ft * 12);
  if (inches === 12) { ft += 1; inches = 0; }
  return inches === 0 ? `${ft}'-0"` : `${ft}'-${inches}"`;
}

/**
 * Given a wall ref + rooms list, return the appropriate opening
 * recommendation. Exterior walls use the room's purpose + side; interior
 * walls use both room names.
 * Returns null when the wall ref can't resolve to known rooms.
 * V2.0.8 — used to populate the Wall+ menu.
 */
export function buildWallRecommendation(wall, rooms) {
  if (!wall || !rooms) return null;
  const byId = new Map(rooms.map((r) => [r.id, r]));
  if (wall.kind === "exterior") {
    const r = byId.get(wall.room_id);
    return r ? recommendExteriorOpening(r.name, wall.side) : null;
  }
  const a = byId.get(wall.room_a);
  const b = byId.get(wall.room_b);
  return a && b ? recommendInteriorOpening(a.name, b.name) : null;
}
